import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getUserDevices, UserDevice, Page } from '../api/admin';
import styles from './CommonPage.module.css';
import { Monitor, ChevronLeft, ChevronRight, X, Search } from 'lucide-react';

const UserDevicesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdParam = searchParams.get('userId');
  const userId = userIdParam ? parseInt(userIdParam, 10) : undefined;
  
  const [data, setData] = useState<Page<UserDevice> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');

  useEffect(() => {
    setPage(0);
  }, [userId]);

  useEffect(() => {
    loadData(page);
  }, [page, userId]); // Keep keyword out of dependency to trigger on explicit search

  const loadData = async (pageNumber: number) => {
    setLoading(true);
    try {
      const result = await getUserDevices(pageNumber, 10, userId, keyword);
      setData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    loadData(0);
  };

  const clearFilter = () => {
    setSearchParams({});
    setKeyword('');
    if (!userId) {
        setTimeout(() => loadData(0), 0);
    }
  };

  const handlePrev = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNext = () => {
    if (data && page < data.totalPages - 1) setPage(page + 1);
  };

  return (
    <div className={styles.container}>
        <div className={styles.header}>
            <div>
            <h1>기기 관리</h1>
            <p>사용자가 연결한 기기 목록을 확인합니다.</p>
            </div>
            <div className={styles.inputBox} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <form onSubmit={handleSearch} className={styles.searchForm} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ position: 'relative', height: '100%' }}>
                        <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                        <input 
                            type="text" 
                            placeholder="사용자명 검색..." 
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            style={{ padding: '8px 8px 8px 36px', height: '100%', borderRadius: '4px', border: '1px solid #d1d5db' }}
                        />
                    </div>
                    <button type="submit" className={styles.primaryBtn} style={{ padding: '8px 16px' }}>검색</button>
                </form>
                {userId && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: '#dbeafe', borderRadius: '16px', color: '#1e40af', fontSize: '0.875rem' }}>
                        <span>사용자 ID: {userId}</span>
                        <button onClick={clearFilter} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex' }}>
                            <X size={14} />
                        </button>
                    </div>
                )}
            </div>
        </div>

        {loading ? (
            <div>로딩 중...</div>
        ) : (
            <>
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                <thead>
                    <tr>
                    <th>사용자 ID</th>
                    <th>접속 번호</th>
                    <th>접속 시간</th>
                    </tr>
                </thead>
                <tbody>
                    {data?.content.map(device => (
                    <tr key={device.id}>
                        <td data-label="사용자 ID" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ padding: '4px', background: '#e0e7ff', borderRadius: '4px' }}>
                                <Monitor size={16} color="#4f46e5" />
                            </div>
                            {device.username}
                        </td>
                        <td data-label="RustDesk ID">{device.onesupportId}</td>
                        <td data-label="접속 시간">{new Date(device.lastActiveAt).toLocaleString()}</td>
                    </tr>
                    ))}
                    {data?.content.length === 0 && (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '24px' }}>연결된 기기가 없습니다.</td>
                        </tr>
                    )}
                </tbody>
                </table>
            </div>

            <div className={styles.pagination}>
                <button onClick={handlePrev} disabled={page === 0}>
                    <ChevronLeft size={16} /> 이전
                </button>
                <span>
                     {data?.number !== undefined ? data.number + 1 : 1} / {data?.totalPages || 1} 페이지
                </span>
                <button onClick={handleNext} disabled={!data || page >= data.totalPages - 1}>
                    다음 <ChevronRight size={16} />
                </button>
            </div>
            </>
        )}
    </div>
  );
};

export default UserDevicesPage;
